import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Building2, 
  Search,
  Plus,
  Copy,
  ExternalLink,
  Mail,
  Send,
  Trash2,
  ListChecks
} from "lucide-react";

const AdminCompanies = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "", cnpj: "", email: "" });
  const [creating, setCreating] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [diagnosticLink, setDiagnosticLink] = useState("");
  const [showDiagnosticsDialog, setShowDiagnosticsDialog] = useState(false);
  const [managingCompany, setManagingCompany] = useState(null);
  const [deletingCompanyId, setDeletingCompanyId] = useState("");
  const [deletingDiagnosticId, setDeletingDiagnosticId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, diagnosticsRes] = await Promise.all([
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/diagnostics`, { withCredentials: true })
      ]);
      setCompanies(companiesRes.data.companies || []);
      setDiagnostics(diagnosticsRes.data.diagnostics || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/companies`, {
        name: newCompany.name,
        cnpj: newCompany.cnpj || null
      }, { withCredentials: true });
      
      toast.success("Empresa cadastrada com sucesso!");
      setShowNewCompany(false);
      setNewCompany({ name: "", cnpj: "", email: "" });
      fetchData();
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error("Erro ao cadastrar empresa");
    } finally {
      setCreating(false);
    }
  };

  const createDiagnosticAndGetLink = async (company) => {
    try {
      // Create new diagnostic
      const response = await axios.post(`${API}/diagnostics`, {
        company_id: company.company_id
      }, { withCredentials: true });
      
      const diagnosticId = response.data.diagnostic_id;
      const link = `${window.location.origin}/diagnostic/${diagnosticId}`;
      
      setSelectedCompany(company);
      setDiagnosticLink(link);
      setShowLinkDialog(true);
      
      toast.success("Diagnóstico criado!");
      fetchData();
    } catch (error) {
      console.error("Error creating diagnostic:", error);
      toast.error("Erro ao criar diagnóstico");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(diagnosticLink);
    toast.success("Link copiado!");
  };

  const getCompanyDiagnostics = (companyId) => {
    return diagnostics.filter(d => d.company_id === companyId);
  };

  const openDiagnosticsManager = (company) => {
    setManagingCompany(company);
    setShowDiagnosticsDialog(true);
  };

  const handleDeleteDiagnostic = async (diagnostic) => {
    const confirmDelete = window.confirm(
      `Excluir o diagnóstico ${diagnostic.diagnostic_id} da empresa ${managingCompany?.name || ""}? Esta ação não pode ser desfeita.`
    );
    if (!confirmDelete) return;

    setDeletingDiagnosticId(diagnostic.diagnostic_id);
    try {
      await axios.delete(`${API}/diagnostics/${diagnostic.diagnostic_id}`, { withCredentials: true });
      toast.success("Diagnóstico excluído com sucesso");
      await fetchData();
    } catch (error) {
      console.error("Error deleting diagnostic:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir diagnóstico");
    } finally {
      setDeletingDiagnosticId("");
    }
  };

  const handleDeleteCompany = async (company) => {
    const confirmDelete = window.confirm(
      `Excluir a empresa ${company.name} e todos os diagnósticos vinculados? Esta ação não pode ser desfeita.`
    );
    if (!confirmDelete) return;

    setDeletingCompanyId(company.company_id);
    try {
      await axios.delete(`${API}/companies/${company.company_id}`, { withCredentials: true });
      toast.success("Empresa excluída com sucesso");
      if (managingCompany?.company_id === company.company_id) {
        setShowDiagnosticsDialog(false);
        setManagingCompany(null);
      }
      await fetchData();
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir empresa");
    } finally {
      setDeletingCompanyId("");
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cnpj?.includes(searchTerm)
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold text-navy">Empresas</h1>
          <Dialog open={showNewCompany} onOpenChange={setShowNewCompany}>
            <DialogTrigger asChild>
              <Button className="bg-navy hover:bg-navy/90" data-testid="new-company-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova Empresa
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif">Cadastrar Nova Empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome da Empresa *</Label>
                  <Input
                    id="companyName"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                    placeholder="Nome da empresa"
                    required
                    data-testid="admin-company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyCnpj">CNPJ (opcional)</Label>
                  <Input
                    id="companyCnpj"
                    value={newCompany.cnpj}
                    onChange={(e) => setNewCompany({...newCompany, cnpj: e.target.value})}
                    placeholder="00.000.000/0000-00"
                    data-testid="admin-company-cnpj-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-navy hover:bg-navy/90"
                  disabled={creating}
                  data-testid="admin-company-submit-btn"
                >
                  {creating ? "Cadastrando..." : "Cadastrar Empresa"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-companies-input"
            />
          </div>
        </div>

        {/* Companies List */}
        {filteredCompanies.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nenhuma empresa encontrada.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCompanies.map((company) => {
              const companyDiags = getCompanyDiagnostics(company.company_id);
              const submittedCount = companyDiags.filter(d => d.status === "submitted").length;
              const draftCount = companyDiags.filter(d => d.status === "draft").length;
              
              return (
                <Card key={company.company_id} className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-navy/10 rounded flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-navy" />
                        </div>
                        <div>
                          <h3 className="font-medium text-navy">{company.name}</h3>
                          {company.cnpj && (
                            <p className="text-sm text-slate-500 font-mono">{company.cnpj}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {companyDiags.length} diagnóstico(s) 
                            {submittedCount > 0 && ` • ${submittedCount} concluído(s)`}
                            {draftCount > 0 && ` • ${draftCount} em andamento`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDiagnosticsManager(company)}
                          data-testid={`manage-diagnostics-${company.company_id}`}
                        >
                          <ListChecks className="h-4 w-4 mr-2" />
                          Diagnósticos
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => createDiagnosticAndGetLink(company)}
                          data-testid={`create-link-${company.company_id}`}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Gerar Link
                        </Button>
                        {companyDiags.some(d => d.status === "submitted") && (
                          <Link to={`/admin/results/${companyDiags.find(d => d.status === "submitted")?.diagnostic_id}`}>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteCompany(company)}
                          disabled={deletingCompanyId === company.company_id}
                          data-testid={`delete-company-${company.company_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {deletingCompanyId === company.company_id ? "Excluindo..." : "Excluir"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Link Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Link do Diagnóstico</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Compartilhe este link com a empresa <strong>{selectedCompany?.name}</strong> para que possam responder o diagnóstico:
              </p>
              
              <div className="flex items-center gap-2">
                <Input
                  value={diagnosticLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={copyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-sm">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> O cliente precisa estar logado para acessar o diagnóstico. 
                  Se ainda não tiver conta, precisará criar uma.
                </p>
              </div>
              
              <Button 
                className="w-full bg-navy hover:bg-navy/90"
                onClick={() => {
                  const subject = encodeURIComponent("Diagnóstico Empresarial - Credita CGE");
                  const body = encodeURIComponent(`Olá,\n\nVocê foi convidado a realizar o Diagnóstico Empresarial da Credita CGE.\n\nAcesse o link abaixo para responder:\n${diagnosticLink}\n\nAtenciosamente,\nEquipe Credita CGE`);
                  window.open(`mailto:?subject=${subject}&body=${body}`);
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Enviar por E-mail
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showDiagnosticsDialog} onOpenChange={setShowDiagnosticsDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif">
                Diagnósticos da empresa {managingCompany?.name || ""}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2 max-h-[65vh] overflow-auto pr-1">
              {!managingCompany ? (
                <p className="text-sm text-slate-500">Nenhuma empresa selecionada.</p>
              ) : getCompanyDiagnostics(managingCompany.company_id).length === 0 ? (
                <p className="text-sm text-slate-500">Essa empresa não possui diagnósticos.</p>
              ) : (
                getCompanyDiagnostics(managingCompany.company_id)
                  .sort((a, b) => new Date(b.started_at || 0).getTime() - new Date(a.started_at || 0).getTime())
                  .map((diagnostic) => (
                    <div key={diagnostic.diagnostic_id} className="rounded border border-slate-200 p-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-navy">{diagnostic.diagnostic_id}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={diagnostic.status === "submitted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                            {diagnostic.status === "submitted" ? "Concluído" : "Rascunho"}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            Início: {diagnostic.started_at ? new Date(diagnostic.started_at).toLocaleString("pt-BR") : "-"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {diagnostic.status === "submitted" && (
                          <Link to={`/admin/results/${diagnostic.diagnostic_id}`}>
                            <Button variant="outline" size="sm">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Ver Resultado
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteDiagnostic(diagnostic)}
                          disabled={deletingDiagnosticId === diagnostic.diagnostic_id}
                          data-testid={`delete-diagnostic-${diagnostic.diagnostic_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {deletingDiagnosticId === diagnostic.diagnostic_id ? "Excluindo..." : "Excluir"}
                        </Button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCompanies;
